import React, { useState } from "react";
import { api } from "../api/api";
import UnderlyingSelect from "./UnderlyingSelect";
import { NIFTY50_STOCKS } from "../constants/nifty50";
import { MCX_COMMODITIES } from "../constants/mcxCommodities";
import { INDEX_UNDERLYINGS } from "../constants/indexOptions";
import "./CreateStrategyForm.css";

// Per-segment underlying picker config + default underlying.
// "OPTIONS" (Stock Option tab) and "INDEX_OPTIONS" (Index Option tab) used
// to be one combined dashboard -- they're split now, so OPTIONS is just the
// flat stock list (no pinned index entry anymore) and INDEX_OPTIONS is its
// own flat list of indices. MCX/STOCK have no index concept either, just a
// flat searchable list of their own instruments.
const SEGMENT_UNDERLYING = {
  INDEX_OPTIONS: { options: INDEX_UNDERLYINGS, indexOption: null, placeholder: "Filter indices...", default: INDEX_UNDERLYINGS[0] },
  OPTIONS: { options: NIFTY50_STOCKS, indexOption: null, placeholder: "Filter stocks...", default: NIFTY50_STOCKS[0] },
  MCX: { options: MCX_COMMODITIES, indexOption: null, placeholder: "Filter commodities...", default: MCX_COMMODITIES[0] },
  STOCK: { options: NIFTY50_STOCKS, indexOption: null, placeholder: "Filter stocks...", default: NIFTY50_STOCKS[0] },
  // Index underlyings as the primary list -- index options are the dominant
  // real-world option-selling use case (deep liquidity, cash-settled, no
  // physical-delivery risk on assignment) versus single-stock options.
  OPTION_SELLING: { options: INDEX_UNDERLYINGS, indexOption: null, placeholder: "Filter indices...", default: INDEX_UNDERLYINGS[0] },
};

// NSE cash/options/futures run 09:15-15:30 IST, so the original defaults
// (entry 09:17:55, auto-exit 15:23:55) fit INDEX_OPTIONS/OPTIONS/STOCK
// fine. MCX's non-agri session (bullion/energy/base metals) runs until
// ~23:30 IST -- reusing the NSE auto-exit default there meant the
// strategy's own auto-exit time had already passed by the time most people
// started it in the afternoon/evening, so it logged
// "no_entry_before_auto_exit_time" and never even tried to enter. Agri
// commodities close earlier (~17:00), so adjust this if that's what you're
// trading.
const SEGMENT_TIMES = {
  INDEX_OPTIONS: { entryTime: "09:17:55", autoExitTime: "15:23:55" },
  OPTIONS: { entryTime: "09:17:55", autoExitTime: "15:23:55" },
  STOCK: { entryTime: "09:17:55", autoExitTime: "15:23:55" },
  MCX: { entryTime: "09:05:00", autoExitTime: "23:15:00" },
  OPTION_SELLING: { entryTime: "09:17:55", autoExitTime: "15:23:55" },
};

const DEFAULTS = {
  // underlying always gets overridden by the segment's own default at init
  // (see SEGMENT_UNDERLYING) -- this is just a fallback.
  aliasName: "", underlying: "NIFTY 50", modeSelection: "Long", atmSelection: "Spot",
  entryTime: "09:17:55", productType: "MIS",
  alaBuyingUpperLevel: "", alaBuyingLowerLevel: "",
  tradingMode: "Paper",

  strike1st: "", strike2nd: "",

  contractMode: "Entry by ATM Points", ceStrikeoutOn: true, ceStrikeout: "0",
  peStrikeoutOn: true, peStrikeout: "0", reEntry: false,

  lotsNo: "1",

  alaSellUpperOn: false, alaSellUpper: "20", alaSellLowerOn: false, alaSellLower: "20",

  autoExitOn: true, autoExitTime: "15:23:55",
  maxProfitOn: true, maxProfit: "50000",
  maxLossOn: true, maxLoss: "10000",

  wtOn: true, wtValue: "50", wtUnit1: "%t", wtUnit2: "%",
  cTargetOn: false, cTarget: "20", cTargetUnit: "%",
  targetOn: false, target: "10",

  vwapOn: true, vwapTimeframe: "minute", deltaRangeUpper: "1", deltaRangeLower: "1",

  slOn: true, slValue: "20", slUnit: "%", reducedSL: "1", minSL: "1", combinedSLPremium: "",

  trailingOn: true, trailX: "5", trailY: "5", trailUnit: "%",

  // "%" is relative to the leg's own blended entry price, recomputed after
  // each add -- so 1% naturally becomes a staircase measured from wherever
  // the last add happened, not from the original entry.
  // Fixed 6-rung ladder: "if price moves favorably by X% of the first
  // lot's fill price, add N lots" -- each rung fires at most once per
  // trade, measured against the frozen original fill (not a staircasing
  // blended average). See strategy_engine._maybe_pyramid().
  pyramidOn: true, lotsMax: "5",
  pyramidRung1Pct: "5", pyramidRung1Lots: "1",
  pyramidRung2Pct: "10", pyramidRung2Lots: "1",
  pyramidRung3Pct: "15", pyramidRung3Lots: "1",
  pyramidRung4Pct: "20", pyramidRung4Lots: "1",
  pyramidRung5Pct: "25", pyramidRung5Lots: "1",
  pyramidRung6Pct: "30", pyramidRung6Lots: "1",

  pyramidSLOn: false, pyramidSLValue: "1", pyramidSLUnit: "%",
  pyramidTrailX: "2", pyramidTrailY: "1", pyramidTrailUnit: "%",
  checkPreStrike: false,

  macdOn: false, macdSignalOn: true, macdFast: "12", macdSlow: "26", macdSignal: "9",
  macdHistogramOn: true, macdHistogramCount: "1",

  conditionTimeframe: "5",
  rsiOn: true, rsiPeriod: "14", rsiLongEntry: "70", rsiShortEntry: "30",
  rsiLongExit: "55", rsiShortExit: "40",
  emaOn: true, emaPeriod: "9",

  // Entry levels: 38.2% is the default pullback entry. Exit levels: 61.8%
  // is the default profit target -- entering on a shallow retracement,
  // exiting into the deeper one, is the classic two-level Fib trade. Both
  // sets are independently configurable per strategy (see the Fibonacci
  // Retracement Entry panel below and the Fibonacci Target Exit panel
  // under Exit Condition).
  fibOn: false,
  fib236On: false, fib382On: true, fib500On: false, fib618On: false, fib786On: false,
  fibExit236On: false, fibExit382On: false, fibExit500On: false, fibExit618On: true, fibExit786On: false,

  // SMC (Smart Money Concepts): liquidity sweep + break-of-structure entry.
  // v1 enters immediately on BOS confirmation (no separate wait for a
  // pullback into an order block/FVG zone -- that needs a second, faster
  // timeframe and isn't built yet). Stop/target are absolute underlying
  // price levels computed at BOS time, not the generic %/Pts Stop Loss --
  // leave Stop Loss/Trailing/Fib off for an SMC strategy, this is its own
  // self-contained exit mechanism. Two trading windows + a daily trade cap
  // are also SMC-specific -- see the panel below for what each field does.
  smcOn: false,
  smcSwingLookback: "3", smcOpeningRangeMinutes: "15",
  smcStopBufferPts: "30", smcMinRR: "2", smcSweepStaleCandles: "8",
  smcMaxTradesPerDay: "2",
  smcWindow1Start: "09:30:00", smcWindow1End: "11:00:00",
  smcWindow2Start: "13:45:00", smcWindow2End: "15:00:00",
  smcBlockedDates: "",

  hedgeOn: false, hedgeLots: "6", hedgeMode: "Entry by ATM Points", hedgeStrikeout: "0",

  protectMode: "Trail Max Loss", trailMaxLossX: "500", trailMaxLossY: "500",

  entryOrderType: "LIMIT", entrySecondsToModify: "1", entryIfNotFilled: "Revise Limit Price",
  exitOrderType: "LIMIT", exitSecondsToModify: "1", exitIfNotFilled: "Revise Limit Price",
};

// Named entry-condition presets, picked from the "Start from a Template"
// dropdown on a brand-new strategy. Each `apply` object is an EXHAUSTIVE
// set of every condition-toggle field (macd/rsi/ema/fib entry+exit levels
// + their sub-params) -- not a partial patch -- so switching templates
// always lands on a clean, fully-determined condition state instead of
// leaving stray toggles on from whatever was picked before. Only condition
// fields are touched; alias, underlying, mode, lots, SL/target/exit config
// are left exactly as they were so picking a template never silently
// resets things you've already filled in.
//
// Deliberately NOT offering a VWAP-named template: the VWAP checkbox/fields
// elsewhere in this form (vwapOn, vwapTimeframe, deltaRangeUpper/Lower)
// aren't read by strategy_engine.py at all -- turning it on wouldn't
// change how the strategy trades. Flagging that here rather than shipping
// a template that implies it does something.
const NO_FIB = {
  fibOn: false,
  fib236On: false, fib382On: false, fib500On: false, fib618On: false, fib786On: false,
  fibExit236On: false, fibExit382On: false, fibExit500On: false, fibExit618On: false, fibExit786On: false,
};
// Enter on the shallow 38.2% pullback, target the deeper 61.8% retracement
// -- the classic two-level Fib trade this whole feature was built around.
const FIB_38_ENTRY_618_EXIT = {
  fibOn: true,
  fib236On: false, fib382On: true, fib500On: false, fib618On: false, fib786On: false,
  fibExit236On: false, fibExit382On: false, fibExit500On: false, fibExit618On: true, fibExit786On: false,
};
const STRATEGY_TEMPLATES = [
  { key: "custom", label: "Custom (no template)", description: "Leave conditions as-is and configure everything yourself." },
  {
    key: "rsi_ema_macd",
    label: "RSI + EMA + MACD Strategy",
    description: "Classic three-indicator confirmation -- all three must agree before entry.",
    apply: {
      rsiOn: true, rsiPeriod: "14", rsiLongEntry: "70", rsiShortEntry: "30", rsiLongExit: "55", rsiShortExit: "40",
      emaOn: true, emaPeriod: "9",
      macdOn: true, macdSignalOn: true, macdFast: "12", macdSlow: "26", macdSignal: "9", macdHistogramOn: true, macdHistogramCount: "1",
      ...NO_FIB,
    },
  },
  {
    key: "rsi_ema",
    label: "RSI + EMA Strategy",
    description: "Lighter two-indicator version -- RSI momentum with an EMA trend filter, no MACD.",
    apply: {
      rsiOn: true, rsiPeriod: "14", rsiLongEntry: "70", rsiShortEntry: "30", rsiLongExit: "55", rsiShortExit: "40",
      emaOn: true, emaPeriod: "9",
      macdOn: false,
      ...NO_FIB,
    },
  },
  {
    key: "macd_only",
    label: "MACD Strategy",
    description: "MACD line crossing above/below its signal line, nothing else gating entry.",
    apply: {
      rsiOn: false, emaOn: false,
      macdOn: true, macdSignalOn: true, macdFast: "12", macdSlow: "26", macdSignal: "9", macdHistogramOn: true, macdHistogramCount: "1",
      ...NO_FIB,
    },
  },
  {
    key: "fibonacci",
    label: "Fibonacci Retracement Strategy",
    description: "Enters on a wick-and-reclaim at the 38.2% retracement, targets the 61.8% level. No RSI/EMA/MACD filter.",
    apply: { rsiOn: false, emaOn: false, macdOn: false, ...FIB_38_ENTRY_618_EXIT },
  },
  {
    key: "rsi_fibonacci",
    label: "RSI + Fibonacci Confluence Strategy",
    description: "Same 38.2%-in / 61.8%-out Fib trade, but entry only fires if RSI also agrees -- fewer, more selective signals.",
    apply: {
      rsiOn: true, rsiPeriod: "14", rsiLongEntry: "70", rsiShortEntry: "30", rsiLongExit: "55", rsiShortExit: "40",
      emaOn: false, macdOn: false,
      ...FIB_38_ENTRY_618_EXIT,
    },
  },
  {
    key: "smc",
    label: "SMC Liquidity Sweep + BOS (Nifty Intraday)",
    description: "Liquidity sweep of PDH/PDL/opening range, confirmed by a break of structure. Own stop/target -- turns off Stop Loss/Trailing/Fib so they don't conflict.",
    // Exception to the "only touch condition fields" rule above: SMC's
    // stop/target are absolute price levels computed at BOS confirmation,
    // not the generic %/Pts Stop Loss -- leaving that on would fight with
    // it, so this template explicitly turns off SL/Trailing/Pyramid/Fib/
    // Max Profit/Max Loss too. It's a self-contained risk model by design.
    apply: {
      rsiOn: false, emaOn: false, macdOn: false, ...NO_FIB,
      smcOn: true,
      smcSwingLookback: "3", smcOpeningRangeMinutes: "15",
      smcStopBufferPts: "30", smcMinRR: "2", smcSweepStaleCandles: "8",
      smcMaxTradesPerDay: "2",
      smcWindow1Start: "09:30:00", smcWindow1End: "11:00:00",
      smcWindow2Start: "13:45:00", smcWindow2End: "15:00:00",
      smcBlockedDates: "",
      slOn: false, trailingOn: false, pyramidOn: false,
      maxProfitOn: false, maxLossOn: false,
    },
  },
];

function Row({ label, children }) {
  return (
    <>
      <span className="f-label">{label}</span>
      <div className="field-row">{children}</div>
    </>
  );
}

function Panel({ title, children, style }) {
  return (
    <div className="panel" style={style}>
      {title && <div className="panel-title">{title}</div>}
      <div className="field-grid">{children}</div>
    </div>
  );
}

export default function CreateStrategyForm({ existing, onSaved, onCancel, segment = "OPTIONS" }) {
  const underlyingCfg = SEGMENT_UNDERLYING[segment] || SEGMENT_UNDERLYING.OPTIONS;
  const timesCfg = SEGMENT_TIMES[segment] || SEGMENT_TIMES.OPTIONS;
  // Index Option and Stock Option are two tabs over the same underlying
  // trading logic (buy ATM(+offset) CE on Long, PE on Short) -- they're
  // only distinguished by which underlying list the picker shows.
  const isAnyOptions = segment === "OPTIONS" || segment === "INDEX_OPTIONS";
  const isOptionSelling = segment === "OPTION_SELLING";
  // MCX also buys ATM(+offset) CE/PE (referenced off the commodity's
  // near-month future instead of a spot index) -- so it shares the ATM
  // fetch + Contract Selection UI too, just not Hedge/Change Strike Points.
  // STOCK is the only segment that's a literal buy/sell of the instrument.
  const usesOptionChain = isAnyOptions || segment === "MCX" || isOptionSelling;
  const [cfg, setCfg] = useState({
    ...DEFAULTS,
    underlying: underlyingCfg.default,
    entryTime: timesCfg.entryTime,
    autoExitTime: timesCfg.autoExitTime,
    segment,
    ...(existing?.config || {}),
  });
  // OPTION_SELLING keeps the same Long=bullish/Short=bearish meaning as
  // every other segment, just expressed by SELLING the opposite option
  // type instead of buying the matching one (Long sells PE, Short sells
  // CE) -- see instrument_resolver.resolve_trade_instrument()'s docstring.
  const soldOrBoughtType = isOptionSelling
    ? (cfg.modeSelection === "Long" ? "PE" : "CE")
    : (cfg.modeSelection === "Long" ? "CE" : "PE");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [atmInfo, setAtmInfo] = useState(null);
  const [atmError, setAtmError] = useState("");
  const [atmBusy, setAtmBusy] = useState(false);
  // Option Chain Preview (Option Selling tab only) -- on-demand snapshot,
  // not live/streaming, so it's a separate fetch from the ATM box above.
  const [chainInfo, setChainInfo] = useState(null);
  const [chainError, setChainError] = useState("");
  const [chainBusy, setChainBusy] = useState(false);
  // Template picker only makes sense on a brand-new strategy -- editing an
  // existing one should show exactly what's saved, not tempt you into
  // silently overwriting it with a preset.
  const [templateKey, setTemplateKey] = useState("custom");

  const bind = (key) => ({
    value: cfg[key],
    onChange: (e) => setCfg((c) => ({ ...c, [key]: e.target.value })),
  });
  const bindCheck = (key) => ({
    checked: cfg[key],
    onChange: (e) => setCfg((c) => ({ ...c, [key]: e.target.checked })),
  });

  const applyTemplate = (key) => {
    setTemplateKey(key);
    const tpl = STRATEGY_TEMPLATES.find((t) => t.key === key);
    if (tpl?.apply) setCfg((c) => ({ ...c, ...tpl.apply }));
  };

  const fetchAtm = async () => {
    setAtmBusy(true); setAtmError(""); setAtmInfo(null);
    try {
      const data = await api.getAtmInfo(cfg.underlying, segment);
      setAtmInfo(data);
    } catch (err) {
      setAtmError(err.message);
    } finally {
      setAtmBusy(false);
    }
  };

  const fetchChain = async () => {
    setChainBusy(true); setChainError(""); setChainInfo(null);
    try {
      const data = await api.getOptionChain(cfg.underlying, segment);
      setChainInfo(data);
    } catch (err) {
      setChainError(err.message);
    } finally {
      setChainBusy(false);
    }
  };

  const save = async () => {
    setBusy(true); setStatus(null);
    try {
      const payload = { ...cfg, segment };
      if (existing?.id) await api.updateStrategy(existing.id, payload);
      else await api.createStrategy(payload);
      setStatus({ ok: true, msg: "Strategy saved." });
      if (onSaved) onSaved();
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="strategy-form">
      {status && <div className={`status-banner ${status.ok ? "ok" : "err"}`}>{status.msg}</div>}

      {!existing && (
        <div className="section">
          <div className="section-title">Start from a Template</div>
          <Panel>
            <Row label="Template">
              <select className="f-select" value={templateKey} onChange={(e) => applyTemplate(e.target.value)}>
                {STRATEGY_TEMPLATES.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
            </Row>
            <Row label="">
              <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
                {STRATEGY_TEMPLATES.find((t) => t.key === templateKey)?.description}
                {" "}Only sets the Entry Condition toggles below (RSI/EMA/MACD/Fibonacci) -- everything else
                (underlying, lots, stop loss, targets) is still yours to fill in and review before saving.
              </span>
            </Row>
          </Panel>
        </div>
      )}

      {/* =============================================================
          SECTION 1 -- UNDERLYING SELECTION
          What you're trading and how much: instrument, strike/contract
          picked, mode, and size. Nothing entry- or exit-timing related
          lives here.
      ============================================================= */}
      <div className="section">
        <div className="section-title">Underlying Selection</div>
        <div className="strategy-columns">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Panel>
              <Row label="Alias Name"><input className="f-input" {...bind("aliasName")} /></Row>
              <Row label="Underlying">
                <UnderlyingSelect
                  value={cfg.underlying}
                  onChange={(v) => setCfg((c) => ({ ...c, underlying: v }))}
                  options={underlyingCfg.options}
                  indexOption={underlyingCfg.indexOption}
                  indexLabel={underlyingCfg.indexLabel}
                  placeholder={underlyingCfg.placeholder}
                />
              </Row>
              <Row label="Mode Selection">
                <select className="f-select" {...bind("modeSelection")}>
                  <option>Long</option>
                  <option>Short</option>
                </select>
                {isOptionSelling && (
                  <span className="f-unit">{cfg.modeSelection === "Long" ? "(Sell PE — bullish)" : "(Sell CE — bearish)"}</span>
                )}
                {usesOptionChain && !isOptionSelling && (
                  <span className="f-unit">{cfg.modeSelection === "Long" ? "(Buy CE)" : "(Buy PE)"}</span>
                )}
                {segment === "STOCK" && (
                  <span className="f-unit">{cfg.modeSelection === "Long" ? "(Buy to open)" : "(Sell to open)"}</span>
                )}
              </Row>
              {usesOptionChain && (
                <Row label="ATM Selection">
                  <span className="f-unit" style={{ flex: 1 }}>
                    {segment === "MCX"
                      ? "Referenced off the near-month future (MCX has no spot index)"
                      : "Spot"}
                  </span>
                  <button type="button" className="btn btn-secondary" style={{ padding: "5px 10px", fontSize: 11 }} onClick={fetchAtm} disabled={atmBusy}>
                    {atmBusy ? "..." : "Fetch"}
                  </button>
                </Row>
              )}
              {usesOptionChain && atmInfo && (
                <Row label="ATM Strike / Price">
                  <input className="f-input narrow" readOnly value={atmInfo.atm_strike} />
                  <input
                    className="f-input narrow"
                    readOnly
                    value={soldOrBoughtType === "CE" ? atmInfo.ce.price : atmInfo.pe.price}
                  />
                  <span className="f-unit">({soldOrBoughtType}) · exp {atmInfo.expiry}</span>
                </Row>
              )}
              {usesOptionChain && atmError && <Row label=""><span style={{ color: "var(--danger)", fontSize: 11 }}>{atmError}</span></Row>}
              <Row label="Product Type">
                <select className="f-select" {...bind("productType")}>
                  <option>MIS</option>
                  <option>NRML</option>
                  <option>CNC</option>
                </select>
              </Row>
              <Row label="Trading Mode">
                <select className="f-select" {...bind("tradingMode")}>
                  <option>Paper</option>
                  <option>Real</option>
                </select>
                {cfg.tradingMode === "Real" && (
                  <span style={{ color: "var(--warn)", fontSize: 10.5 }}>Places real orders when started</span>
                )}
              </Row>
            </Panel>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {isAnyOptions && (
              <Panel title="Change Strike Points">
                <Row label="1st strike point"><input className="f-input narrow" {...bind("strike1st")} /></Row>
                <Row label="2nd"><input className="f-input narrow" {...bind("strike2nd")} /></Row>
              </Panel>
            )}

            {usesOptionChain && (
              <Panel title="Contract Selection">
                <Row label="Mode">
                  <select className="f-select" {...bind("contractMode")}>
                    <option>Entry by ATM Points</option>
                    <option>Entry by Premium</option>
                  </select>
                </Row>
                <Row label="CE Strikeout">
                  <input type="checkbox" className="f-checkbox" {...bindCheck("ceStrikeoutOn")} />
                  <input className="f-input narrow" {...bind("ceStrikeout")} />
                  <span className="f-help" title="Offset from the reference price used to pick the Long-side (CE) strike">?</span>
                </Row>
                <Row label="PE Strikeout">
                  <input type="checkbox" className="f-checkbox" {...bindCheck("peStrikeoutOn")} />
                  <input className="f-input narrow" {...bind("peStrikeout")} />
                </Row>
                {isAnyOptions && (
                  <Row label="Re-Entry">
                    <input type="checkbox" className="f-checkbox" disabled {...bindCheck("reEntry")} />
                  </Row>
                )}
              </Panel>
            )}

            {isOptionSelling && (
              <Panel title="Option Chain Preview">
                <Row label="">
                  <span className="f-unit" style={{ flex: 1 }}>Read-only snapshot for reference -- not live-streaming, and not what the engine trades off (it resolves the strike fresh at entry time).</span>
                  <button type="button" className="btn btn-secondary" style={{ padding: "5px 10px", fontSize: 11 }} onClick={fetchChain} disabled={chainBusy}>
                    {chainBusy ? "..." : "Fetch Option Chain"}
                  </button>
                </Row>
                {chainError && <Row label=""><span style={{ color: "var(--danger)", fontSize: 11 }}>{chainError}</span></Row>}
                {chainInfo && (
                  <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 6 }}>
                    <div style={{ fontSize: 11, color: "var(--text-dim)", marginBottom: 6 }}>
                      Spot {chainInfo.spot} · ATM {chainInfo.atm_strike} · exp {chainInfo.expiry}
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "right", padding: "4px 6px", color: "var(--text-dim)" }}>CE LTP</th>
                          <th style={{ textAlign: "center", padding: "4px 6px", color: "var(--text-dim)" }}>Strike</th>
                          <th style={{ textAlign: "left", padding: "4px 6px", color: "var(--text-dim)" }}>PE LTP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {chainInfo.rows.map((row) => (
                          <tr key={row.strike} style={row.strike === chainInfo.atm_strike ? { background: "var(--panel-alt)" } : undefined}>
                            <td style={{ textAlign: "right", padding: "4px 6px", fontFamily: "var(--font-mono)" }}>
                              {row.ce?.ltp != null ? row.ce.ltp.toFixed(2) : "—"}
                            </td>
                            <td style={{ textAlign: "center", padding: "4px 6px", fontWeight: 600 }}>{row.strike}</td>
                            <td style={{ textAlign: "left", padding: "4px 6px", fontFamily: "var(--font-mono)" }}>
                              {row.pe?.ltp != null ? row.pe.ltp.toFixed(2) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Panel title={segment === "STOCK" ? "Quantity Configuration" : "Lots Configuration"}>
              <Row label={segment === "STOCK" ? "Quantity (shares)" : "No of lots"}>
                <input className="f-input narrow" {...bind("lotsNo")} />
              </Row>
            </Panel>
          </div>
        </div>
      </div>

      {/* =============================================================
          SECTION 2 -- ENTRY CONDITION
          Everything gating when/how a position is opened: timing,
          entry-side filters (RSI/EMA/MACD/VWAP/W&T), position sizing
          via pyramiding, hedge leg, and the entry order's execution
          settings. No exit-only fields (targets, stop loss, square-off
          thresholds) live in this section.
      ============================================================= */}
      <div className="section">
        <div className="section-title">Entry Condition</div>
        <div className="strategy-columns">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Panel title="Entry Timing">
              <Row label="Entry Time"><input className="f-input time" type="time" step="1" {...bind("entryTime")} /></Row>
              <Row label="ALA Buying Upper Level"><input className="f-input" {...bind("alaBuyingUpperLevel")} /></Row>
              <Row label="ALA Buying Lower Level"><input className="f-input" {...bind("alaBuyingLowerLevel")} /></Row>
            </Panel>

            <Panel title="Wait & Trade">
              <Row label="Enable W&T">
                <input type="checkbox" className="f-checkbox" {...bindCheck("wtOn")} />
                <input className="f-input narrow" {...bind("wtValue")} />
                <select className="f-select" style={{ width: 60 }} {...bind("wtUnit1")}>
                  <option>%t</option><option>Pts</option>
                </select>
                <select className="f-select" style={{ width: 50 }} {...bind("wtUnit2")}>
                  <option>%</option><option>Pts</option>
                </select>
              </Row>
            </Panel>

            <Panel title="Enable VWAP">
              <Row label="Enable"><input type="checkbox" className="f-checkbox" {...bindCheck("vwapOn")} /></Row>
              <Row label="Timeframe">
                <select className="f-select" {...bind("vwapTimeframe")}>
                  <option>minute</option><option>5 minute</option><option>15 minute</option>
                </select>
              </Row>
              <Row label="Delta Range Upper"><input className="f-input narrow" {...bind("deltaRangeUpper")} /></Row>
              <Row label="Delta Lower Range"><input className="f-input narrow" {...bind("deltaRangeLower")} /></Row>
            </Panel>

            {(isAnyOptions || isOptionSelling) && (
              <Panel title="Hedge">
                <Row label="Enable Hedge">
                  <input type="checkbox" className="f-checkbox" {...bindCheck("hedgeOn")} />
                  {isOptionSelling && (
                    <span className="f-unit">Buys a further-OTM {soldOrBoughtType} to cap risk on the sold leg</span>
                  )}
                  {isAnyOptions && (
                    <span className="f-unit" style={{ color: "var(--text-dim)" }}>Not used in Long/Short mode -- see Option Selling tab</span>
                  )}
                </Row>
                <Row label="No of Lots"><input className="f-input narrow" disabled={!cfg.hedgeOn} {...bind("hedgeLots")} /></Row>
              </Panel>
            )}

            {(isAnyOptions || isOptionSelling) && (
              <Panel title="Hedge Contract Selection">
                <Row label="Mode">
                  <select className="f-select" disabled={!cfg.hedgeOn} {...bind("hedgeMode")}>
                    <option>Entry by ATM Points</option>
                    <option>Entry by Premium</option>
                  </select>
                </Row>
                <Row label="Hedge Strikeout">
                  <input className="f-input narrow" disabled={!cfg.hedgeOn} {...bind("hedgeStrikeout")} />
                  <span className="f-help">?</span>
                </Row>
              </Panel>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Panel title="Pyramid Parameters">
              <Row label="Enable Pyramid"><input type="checkbox" className="f-checkbox" {...bindCheck("pyramidOn")} /></Row>
              <Row label="Max lots (safety cap, total)"><input className="f-input narrow" {...bind("lotsMax")} /></Row>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <Row key={n} label={`Rung ${n}`}>
                  <span className="f-unit">if price &gt;</span>
                  <input className="f-input narrow" style={{ width: 55 }} {...bind(`pyramidRung${n}Pct`)} />
                  <span className="f-unit">% of 1st lot price, add</span>
                  <input className="f-input narrow" style={{ width: 55 }} {...bind(`pyramidRung${n}Lots`)} />
                  <span className="f-unit">lot(s)</span>
                </Row>
              ))}
              <Row label="">
                <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
                  Each rung fires at most once per trade, and every rung is checked every poll -- a fast move
                  that jumps past several rungs at once still fires all of them. "% of 1st lot price" is
                  always measured against the very first fill, never the blended average after later adds, so
                  each rung means the same thing for the whole life of the trade. Leave a rung's % or lots
                  blank/zero to disable just that rung. Max lots is a hard ceiling on top of the ladder --
                  the last add is clamped down (or skipped) rather than ever overshooting it.
                </span>
              </Row>
            </Panel>

            <Panel title="MACD Parameters">
              <Row label="MACD">
                <input type="checkbox" className="f-checkbox" {...bindCheck("macdOn")} />
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 8 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("macdSignalOn")} /> MACD &gt; Signal Line
                </label>
              </Row>
              <Row label="Fast / Slow / Signal">
                <input className="f-input narrow" {...bind("macdFast")} />
                <input className="f-input narrow" {...bind("macdSlow")} />
                <input className="f-input narrow" {...bind("macdSignal")} />
              </Row>
              <Row label="MACD Histogram">
                <input type="checkbox" className="f-checkbox" {...bindCheck("macdHistogramOn")} />
              </Row>
              <Row label="No. of histogram"><input className="f-input narrow" {...bind("macdHistogramCount")} /></Row>
            </Panel>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Panel title="RSI + EMA Entry Conditions">
              <Row label="Timeframe (all conditions)">
                <select className="f-select" {...bind("conditionTimeframe")}>
                  <option value="1">1 min</option>
                  <option value="3">3 min</option>
                  <option value="5">5 min</option>
                  <option value="15">15 min</option>
                </select>
              </Row>
              <Row label="Enable RSI">
                <input type="checkbox" className="f-checkbox" {...bindCheck("rsiOn")} />
                <span className="f-unit">Period</span>
                <input className="f-input narrow" {...bind("rsiPeriod")} />
              </Row>
              <Row label="RSI Entry (Long > / Short <)">
                <input className="f-input narrow" {...bind("rsiLongEntry")} />
                <input className="f-input narrow" {...bind("rsiShortEntry")} />
              </Row>
              <Row label="Enable EMA Filter">
                <input type="checkbox" className="f-checkbox" {...bindCheck("emaOn")} />
                <span className="f-unit">Period</span>
                <input className="f-input narrow" {...bind("emaPeriod")} />
              </Row>
              <Row label="">
                <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
                  Long needs close above EMA; Short needs close below EMA. This same "Enable RSI" toggle and
                  period also drive the RSI Square-off exit -- see Exit Condition below.
                </span>
              </Row>
            </Panel>

            <Panel title="Fibonacci Retracement Entry">
              <Row label="Enable Fib Entry">
                <input type="checkbox" className="f-checkbox" {...bindCheck("fibOn")} />
              </Row>
              <Row label="Levels to watch">
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fib236On")} /> 23.6%
                </label>
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 10 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fib382On")} /> 38.2%
                </label>
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 10 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fib500On")} /> 50%
                </label>
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 10 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fib618On")} /> 61.8%
                </label>
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 10 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fib786On")} /> 78.6%
                </label>
              </Row>
              <Row label="">
                <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
                  Levels start from yesterday's high/low, and expand to include today's own high/low once today
                  trades beyond either side (like a Fibonacci extension on breakout days). Entry fires when a
                  candle wicks through a checked level and closes back on the trade side (Long above / Short
                  below) -- a rejection off that level, not just a touch. ANDed with RSI/EMA/MACD above if those
                  are also enabled. This same "Enable Fib Entry" toggle also gates the Fibonacci Target Exit --
                  see Exit Condition below.
                </span>
              </Row>
            </Panel>

            <Panel title="Smart Money Concepts (SMC)">
              <Row label="Enable SMC">
                <input type="checkbox" className="f-checkbox" {...bindCheck("smcOn")} />
                <span className="f-unit">Nifty-oriented, works on any INDEX_OPTIONS underlying</span>
              </Row>
              <Row label="Swing lookback (candles each side)">
                <input className="f-input narrow" {...bind("smcSwingLookback")} />
              </Row>
              <Row label="Opening range (minutes after 9:15)">
                <input className="f-input narrow" {...bind("smcOpeningRangeMinutes")} />
              </Row>
              <Row label="Stop buffer (pts beyond sweep high/low)">
                <input className="f-input narrow" {...bind("smcStopBufferPts")} />
              </Row>
              <Row label="Min Risk:Reward to take the trade">
                <input className="f-input narrow" {...bind("smcMinRR")} />
              </Row>
              <Row label="Sweep goes stale after (candles)">
                <input className="f-input narrow" {...bind("smcSweepStaleCandles")} />
              </Row>
              <Row label="Max trades / day">
                <input className="f-input narrow" {...bind("smcMaxTradesPerDay")} />
              </Row>
              <Row label="Window 1 (Start / End)">
                <input className="f-input time" type="time" step="1" {...bind("smcWindow1Start")} />
                <input className="f-input time" type="time" step="1" {...bind("smcWindow1End")} />
              </Row>
              <Row label="Window 2 (Start / End)">
                <input className="f-input time" type="time" step="1" {...bind("smcWindow2Start")} />
                <input className="f-input time" type="time" step="1" {...bind("smcWindow2End")} />
              </Row>
              <Row label="Blocked dates (comma-separated YYYY-MM-DD)">
                <input className="f-input" placeholder="e.g. 2026-08-06, 2026-08-23" {...bind("smcBlockedDates")} />
              </Row>
              <Row label="">
                <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
                  Watches PDH/PDL, the opening range, and recent swing highs/lows as liquidity pools. Enters
                  immediately once a sweep of one of those levels is followed by a break of structure (v1 -- no
                  wait for a pullback into an order block/FVG zone yet). Stop = sweep extreme + buffer; Target =
                  the opposite liquidity pool; trade is skipped if it doesn't clear Min RR. Direction (Long/Short)
                  is decided per-trade by the sweep, not by Mode Selection above. Trades only inside the two
                  windows below (skipping midday on purpose), up to Max trades/day -- this OWNS Stop Loss/
                  Trailing/Target for the trade, so leave those off elsewhere to avoid two exit systems fighting.
                  Event-day avoidance (RBI/budget/Fed/expiry) isn't automatable here -- there's no economic
                  calendar feed in this app -- so Blocked dates is a manual list you maintain yourself.
                </span>
              </Row>
            </Panel>

            <Panel title="Entry Order Configuration">
              <Row label="Order Type">
                <select className="f-select" {...bind("entryOrderType")}>
                  <option>LIMIT</option>
                </select>
              </Row>
              <Row label="Seconds to modify if not filled"><input className="f-input narrow" {...bind("entrySecondsToModify")} /></Row>
              <Row label="If not filled">
                <select className="f-select" {...bind("entryIfNotFilled")}>
                  <option>Revise Limit Price</option>
                  <option>Convert to Market</option>
                  <option>Cancel Order</option>
                </select>
              </Row>
            </Panel>
          </div>
        </div>
      </div>

      {/* =============================================================
          SECTION 3 -- EXIT CONDITION
          Everything that can close a position: hard cutoffs (time,
          max profit/loss), stop loss + trailing (now default to %),
          pyramid-leg stop loss, RSI square-off, profit protection,
          and the exit order's execution settings.
      ============================================================= */}
      <div className="section">
        <div className="section-title">Exit Condition</div>
        <div className="strategy-columns">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Panel title="Exit Triggers">
              <Row label="Auto Exit Time">
                <input type="checkbox" className="f-checkbox" {...bindCheck("autoExitOn")} />
                <input className="f-input time" type="time" step="1" {...bind("autoExitTime")} />
              </Row>
              <Row label="Max Profit">
                <input type="checkbox" className="f-checkbox" {...bindCheck("maxProfitOn")} />
                <input className="f-input" {...bind("maxProfit")} />
              </Row>
              <Row label="Max Loss">
                <input type="checkbox" className="f-checkbox" {...bindCheck("maxLossOn")} />
                <input className="f-input" {...bind("maxLoss")} />
              </Row>
            </Panel>

            <Panel title="Target">
              <Row label="C Target">
                <input type="checkbox" className="f-checkbox" {...bindCheck("cTargetOn")} />
                <input className="f-input narrow" {...bind("cTarget")} />
                <select className="f-select" style={{ width: 50 }} {...bind("cTargetUnit")}>
                  <option>%</option><option>Pts</option>
                </select>
              </Row>
              <Row label="Target">
                <input type="checkbox" className="f-checkbox" {...bindCheck("targetOn")} />
                <input className="f-input narrow" {...bind("target")} />
              </Row>
            </Panel>

            <Panel title="ALA Selling Exit Levels">
              <Row label="Upper Level">
                <input type="checkbox" className="f-checkbox" disabled {...bindCheck("alaSellUpperOn")} />
                <input className="f-input narrow" disabled {...bind("alaSellUpper")} />
              </Row>
              <Row label="Lower Level">
                <input type="checkbox" className="f-checkbox" disabled {...bindCheck("alaSellLowerOn")} />
                <input className="f-input narrow" disabled {...bind("alaSellLower")} />
              </Row>
            </Panel>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Panel title="Stop Loss Configuration">
              <Row label="Enable Stop Loss">
                <input type="checkbox" className="f-checkbox" {...bindCheck("slOn")} />
                <input className="f-input narrow" {...bind("slValue")} />
                <select className="f-select" style={{ width: 60 }} {...bind("slUnit")}>
                  <option>%</option><option>Pts</option>
                </select>
              </Row>
              <Row label="Reduced SL"><input className="f-input narrow" {...bind("reducedSL")} /></Row>
              <Row label="Min SL"><input className="f-input narrow" {...bind("minSL")} /></Row>
              <Row label="Combined SL Premium"><input className="f-input narrow" {...bind("combinedSLPremium")} /></Row>
            </Panel>

            <Panel title="Stop Loss Trailing Configuration">
              <Row label="Enable Trailing">
                <input type="checkbox" className="f-checkbox" {...bindCheck("trailingOn")} />
                <span className="f-help" title="Move SL up as price moves in your favor">?</span>
              </Row>
              <Row label="Trail X / Y">
                <input className="f-input narrow" {...bind("trailX")} />
                <input className="f-input narrow" {...bind("trailY")} />
                <select className="f-select" style={{ width: 60 }} {...bind("trailUnit")}>
                  <option>%</option><option>Pts</option>
                </select>
              </Row>
              <Row label="">
                <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
                  % is relative to this leg's own entry price (recalculated after pyramid adds).
                </span>
              </Row>
            </Panel>

            <Panel title="Pyramid SL Parameters">
              <Row label="Enable SL">
                <input type="checkbox" className="f-checkbox" {...bindCheck("pyramidSLOn")} />
                <input className="f-input narrow" {...bind("pyramidSLValue")} />
                <select className="f-select" style={{ width: 50 }} {...bind("pyramidSLUnit")}>
                  <option>%</option><option>Pts</option>
                </select>
              </Row>
              <Row label="Trail X / Y">
                <input className="f-input narrow" {...bind("pyramidTrailX")} />
                <input className="f-input narrow" {...bind("pyramidTrailY")} />
                <select className="f-select" style={{ width: 50 }} {...bind("pyramidTrailUnit")}>
                  <option>%</option><option>Pts</option>
                </select>
              </Row>
              <Row label="Check Pre. Strike">
                <input type="checkbox" className="f-checkbox" {...bindCheck("checkPreStrike")} />
              </Row>
            </Panel>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Panel title="RSI Square-off">
              <Row label="Long < / Short >">
                <input className="f-input narrow" {...bind("rsiLongExit")} />
                <input className="f-input narrow" {...bind("rsiShortExit")} />
              </Row>
              <Row label="">
                <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
                  Requires "Enable RSI" turned on under Entry Condition -- uses that same period/timeframe.
                </span>
              </Row>
            </Panel>

            <Panel title="Fibonacci Target Exit">
              <Row label="Exit levels (profit target)">
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fibExit236On")} /> 23.6%
                </label>
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 10 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fibExit382On")} /> 38.2%
                </label>
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 10 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fibExit500On")} /> 50%
                </label>
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 10 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fibExit618On")} /> 61.8%
                </label>
                <label className="f-unit" style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: 10 }}>
                  <input type="checkbox" className="f-checkbox" {...bindCheck("fibExit786On")} /> 78.6%
                </label>
              </Row>
              <Row label="">
                <span style={{ color: "var(--text-dim)", fontSize: 11 }}>
                  Requires "Enable Fib Entry" turned on under Entry Condition. Exits as soon as the underlying's
                  live price reaches a checked level -- using the range frozen at entry, so the target doesn't
                  shift mid-trade even as today's range keeps expanding. Runs alongside Stop Loss / Trailing /
                  Max Profit / Max Loss / Auto Exit below -- whichever fires first closes the trade.
                </span>
              </Row>
            </Panel>

            <Panel title="Protect Profit Per Lot Basis">
              <Row label="Mode">
                <select className="f-select" {...bind("protectMode")}>
                  <option>Trail Max Loss</option>
                  <option>Lock Profit</option>
                </select>
              </Row>
              <Row label="Trail Max Loss X"><input className="f-input" {...bind("trailMaxLossX")} /></Row>
              <Row label="Trail Max Loss Y"><input className="f-input" {...bind("trailMaxLossY")} /></Row>
            </Panel>

            <Panel title="Exit Order Configuration">
              <Row label="Order Type">
                <select className="f-select" {...bind("exitOrderType")}>
                  <option>LIMIT</option>
                </select>
              </Row>
              <Row label="Seconds to modify if not filled"><input className="f-input narrow" {...bind("exitSecondsToModify")} /></Row>
              <Row label="If not filled">
                <select className="f-select" {...bind("exitIfNotFilled")}>
                  <option>Revise Limit Price</option>
                  <option>Convert to Market</option>
                  <option>Cancel Order</option>
                </select>
              </Row>
            </Panel>
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? "Saving..." : "Save"}</button>
      </div>
    </div>
  );
}
